import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Upload, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { playersService } from "@/api/services/player.service";
import { ClubSelect } from "@/components/ClubSelect";
import { NationSelect } from "@/components/NationSelect";
import { toast } from "react-toastify";
import type { Player, CreatePlayerRequest } from "@/types/api.types";

interface PlayerFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  player: Partial<Player> | null;
  isEditing: boolean;
  onSave: (data: CreatePlayerRequest) => void;
  isSaving: boolean;
}

const POSITIONS = ["GK", "DEF", "MID", "FWD"];

export default function PlayerForm({
  open,
  onOpenChange,
  player,
  isEditing,
  onSave,
  isSaving,
}: PlayerFormProps) {
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreatePlayerRequest>({
    defaultValues: {
      name: "",
      position: undefined,
      nationId: undefined,
      clubId: undefined,
      age: undefined,
      jerseyNumber: undefined,
      photo: "",
      difficulty: null,
      careerGoals: null,
      careerAssists: null,
    },
  });

  const [selectedClub, setSelectedClub] = useState<{ id: number; name: string; logoUrl: string | null } | null>(null);
  const [selectedNation, setSelectedNation] = useState<{ id: number; name: string; code: string; flagUrl: string | null } | null>(null);

  useEffect(() => {
    if (player) {
      reset({
        name: player.name || "",
        position: player.position,
        nationId: player.nationId,
        clubId: player.clubId ?? undefined,
        age: player.age ?? undefined,
        jerseyNumber: player.jerseyNumber ?? undefined,
        photo: player.photo || "",
        difficulty: player.difficulty ?? null,
        careerGoals: player.careerGoals ?? null,
        careerAssists: player.careerAssists ?? null,
      });
      setPhotoPreview(player.photo || "");
      setSelectedClub(
        player.club
          ? { id: player.club.id, name: player.club.name, logoUrl: player.club.logoUrl ?? null }
          : null,
      );
      setSelectedNation(
        player.nation
          ? { id: player.nation.id, name: player.nation.name, code: player.nation.code, flagUrl: player.nation.flagUrl ?? null }
          : null,
      );
    } else {
      setSelectedNation(null);
      setSelectedClub(null);
    }
  }, [player, reset]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2MB");
      return;
    }

    setIsUploading(true);
    try {
      const response = await playersService.uploadPhoto(file);
      setValue("photo", response.url);
      setPhotoPreview(response.url);
      toast.success("Photo uploaded successfully");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to upload photo");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setValue("photo", "");
    setPhotoPreview("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            {isEditing ? "Edit Player" : "Add New Player"}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((data) => {
            const clean = {
              name: data.name,
              nationId: data.nationId,
              position: data.position,
              ...(data.jerseyNumber && !Number.isNaN(data.jerseyNumber) && { jerseyNumber: data.jerseyNumber }),
              ...(data.clubId && { clubId: data.clubId }),
              ...(data.age && !Number.isNaN(data.age) && { age: data.age }),
              // When editing, always send photo (null clears it); when creating, only if set
              ...(isEditing ? { photo: data.photo || null } : data.photo ? { photo: data.photo } : {}),
              // Always send difficulty (null clears it on edit; null is fine on create)
              difficulty: data.difficulty ?? null,
              // Career totals — `null` clears, `undefined` would mean "not in form"
              careerGoals:
                data.careerGoals === undefined || data.careerGoals === null || Number.isNaN(data.careerGoals)
                  ? null
                  : data.careerGoals,
              careerAssists:
                data.careerAssists === undefined || data.careerAssists === null || Number.isNaN(data.careerAssists)
                  ? null
                  : data.careerAssists,
            } as CreatePlayerRequest;
            onSave(clean);
          })}
          className="space-y-4"
        >
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-gray-700">
              Player Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              {...register("name", { required: "Player name is required" })}
              placeholder="Lionel Messi"
              className="bg-white border-gray-300"
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Position & Jersey Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700 mb-2">
                Position <span className="text-red-500">*</span>
              </Label>
              <Select
                value={watch("position") || ""}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onValueChange={(val) => setValue("position", val as any)}
              >
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((pos) => (
                    <SelectItem key={pos} value={pos}>
                      {pos}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.position && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.position.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="jerseyNumber" className="text-gray-700">
                Jersey Number
              </Label>
              <Input
                id="jerseyNumber"
                type="number"
                min={1}
                max={99}
                {...register("jerseyNumber", { valueAsNumber: true })}
                placeholder="10"
                className="bg-white border-gray-300"
              />
            </div>
          </div>

          {/* Nation */}
          <div>
            <Label className="text-gray-700 mb-2">
              Nation <span className="text-red-500">*</span>
            </Label>
            <NationSelect
              value={selectedNation ? { name: selectedNation.name, code: selectedNation.code, flagUrl: selectedNation.flagUrl } : null}
              onChange={(n) => {
                setSelectedNation(n ? { id: n.id, name: n.name, code: n.code, flagUrl: n.flagUrl ?? null } : null);
                // nationId is required on submit; submit handler's `as CreatePlayerRequest` cast + backend
                // validation enforce this. undefined here means "cleared" in RHF terms.
                setValue("nationId", n?.id as number);
              }}
            />
          </div>

          {/* Club & Age */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700 mb-2">Club</Label>
              <ClubSelect
                value={selectedClub ? { name: selectedClub.name, logoUrl: selectedClub.logoUrl } : null}
                onChange={(c) => {
                  setSelectedClub(c ? { id: c.id, name: c.name, logoUrl: c.logoUrl ?? null } : null);
                  setValue("clubId", c?.id as number | undefined);
                }}
              />
            </div>
            <div>
              <Label htmlFor="age" className="text-gray-700">Age</Label>
              <Input
                id="age"
                type="number"
                min={15}
                max={50}
                {...register("age", { valueAsNumber: true })}
                placeholder="27"
                className="bg-white border-gray-300"
              />
            </div>
          </div>

          {/* Difficulty — surfaces as a coloured pill on Guess the
              Player and Career Path screens in mobile/web. */}
          <div>
            <Label className="text-gray-700 mb-2">Difficulty</Label>
            <Select
              value={watch("difficulty") ?? "UNRATED"}
              onValueChange={(val) =>
                setValue(
                  "difficulty",
                  val === "UNRATED"
                    ? null
                    : (val as "EASY" | "MEDIUM" | "HARD"),
                )
              }
            >
              <SelectTrigger className="bg-white border-gray-300">
                <SelectValue placeholder="Unrated" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UNRATED">Unrated</SelectItem>
                <SelectItem value="EASY">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                    Easy
                  </span>
                </SelectItem>
                <SelectItem value="MEDIUM">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    Medium
                  </span>
                </SelectItem>
                <SelectItem value="HARD">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                    Hard
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Shown to users on Guess the Player and Career Path. Same
              rating applies to both. Leave Unrated if you don't want
              a pill to show.
            </p>
          </div>

          {/* Career totals — feed Higher or Lower's Goals / Assists tabs.
              Leave blank to fall back to the legacy per-card statValue. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="careerGoals" className="text-gray-700">
                Career Goals
              </Label>
              <Input
                id="careerGoals"
                type="number"
                min={0}
                {...register("careerGoals", { valueAsNumber: true })}
                placeholder="e.g. 821"
                className="bg-white border-gray-300"
              />
            </div>
            <div>
              <Label htmlFor="careerAssists" className="text-gray-700">
                Career Assists
              </Label>
              <Input
                id="careerAssists"
                type="number"
                min={0}
                {...register("careerAssists", { valueAsNumber: true })}
                placeholder="e.g. 365"
                className="bg-white border-gray-300"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            Used by Higher or Lower's Goals / Assists tabs. Leave blank
            for unrated players.
          </p>

          {/* Photo Upload */}
          <div>
            <Label className="text-gray-700">Player Photo</Label>

            {photoPreview && (
              <div className="mt-2 relative inline-block">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-24 h-24 rounded-full object-cover border"
                />
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            <div className="mt-2">
              <label
                htmlFor="photo-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-md cursor-pointer hover:bg-blue-100 transition"
              >
                <Upload className="h-4 w-4" />
                {isUploading ? "Uploading..." : "Upload Photo"}
              </label>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={isUploading}
                className="hidden"
              />
            </div>

            <div className="mt-3">
              <Input
                {...register("photo")}
                placeholder="Or paste photo URL"
                type="url"
                className="bg-white border-gray-300"
                onChange={(e) => setPhotoPreview(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="bg-white pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving || isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || isUploading}>
              {isSaving ? "Saving..." : isEditing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
